/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, Subject, switchMap } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { AccessContractApiService } from '../api/access-contract-api.service';
import { AccessContract } from '../../../lib/models/access-contract.interface';
import { SearchService } from '../vitamui-table/search.service';
import { SnackBarService } from '../components/snack-bar/snack-bar.service';
import { ExternalParametersService } from '../externalParameters.service';
import { ExternalParameters } from '../externalParameters.enum';
import { VitamuiHttpHeaders } from '../vitamui-http-headers.enum';

@Injectable({
  providedIn: 'root',
})
export class AccessContractService extends SearchService<AccessContract> {
  private accessContractApi: AccessContractApiService;
  private snackBarService = inject(SnackBarService);
  private externalParameterService = inject(ExternalParametersService);

  /** Observable of current access contract ID */
  currentAccessContractId$: Observable<string> = this.externalParameterService.getUserExternalParameters().pipe(
    map((parameters) => parameters.get(ExternalParameters.PARAM_ACCESS_CONTRACT)),
    shareReplay(1), //cached
  );
  /** Observable of current access contract */
  currentAccessContract$: Observable<AccessContract> = this.currentAccessContractId$.pipe(
    switchMap((accessContractId) => this.get(accessContractId)),
    shareReplay(1), //cached
  );

  updated = new Subject<AccessContract>();

  constructor() {
    const accessContractApi = inject(AccessContractApiService);

    super(accessContractApi, 'ALL');

    this.accessContractApi = accessContractApi;
  }

  get(id: string): Observable<AccessContract> {
    const headers = new HttpHeaders().append('Content-Type', 'application/json');
    return this.accessContractApi.getOne(encodeURI(id), headers);
  }

  getAll(): Observable<AccessContract[]> {
    const params = new HttpParams().set('embedded', 'ALL');
    return this.accessContractApi.getAllByParams(params);
  }

  getAllForTenant(tenantId: string): Observable<AccessContract[]> {
    // TODO: Cgeck add of tenantId
    const params = new HttpParams().set('embedded', 'ALL');
    const headers = new HttpHeaders().set(VitamuiHttpHeaders.X_TENANT_ID, tenantId);
    return this.accessContractApi.getAllByParams(params, headers);
  }

  exists(name: string): Observable<boolean> {
    const accessContract = { name, identifier: name } as AccessContract;
    return this.accessContractApi.check(accessContract, this.headers);
  }

  existsProperties(properties: { name?: string; identifier?: string }): Observable<any> {
    const existContract: any = {};
    if (properties.name) {
      existContract.name = properties.name;
    }
    if (properties.identifier) {
      existContract.identifier = properties.identifier;
    }

    const context = existContract as AccessContract;
    return this.accessContractApi.check(context, this.headers);
  }

  patch(data: { id: string; [key: string]: any }): Observable<AccessContract> {
    return this.accessContractApi.patch(data).pipe(
      tap((response) => this.updated.next(response)),
      tap({
        next: (response) => {
          this.snackBarService.open({
            message: 'SNACKBAR.ACCESS_CONTRACT_UPDATED',
            translateParams: {
              name: response.name,
            },
            icon: 'vitamui-icon-contrat',
          });
        },
        error: (error) => {
          this.snackBarService.open({ message: error.error.message, translate: false });
        },
      }),
    );
  }

  create(accessContract: AccessContract): Observable<AccessContract> {
    return this.accessContractApi.create(accessContract).pipe(
      tap(
        (response: AccessContract) => {
          this.snackBarService.open({
            message: 'SNACKBAR.ACCESS_CONTRACT_CREATED',
            translateParams: {
              name: response.name,
            },
            icon: 'vitamui-icon-contrat',
          });
        },
        (error) => {
          this.snackBarService.open({ message: error.error.message, translate: false });
        },
      ),
    );
  }

  setTenantId(tenantIdentifier: number) {
    this.headers = new HttpHeaders().set(VitamuiHttpHeaders.X_TENANT_ID, tenantIdentifier.toString());
  }

  public downloadImportAccessContractFileModel(): Observable<HttpResponse<Blob>> {
    return this.accessContractApi.getImportAccessContractFileModel();
  }

  public exportAccessContracts(): Observable<HttpResponse<Blob>> {
    return this.accessContractApi.exportAccessContracts();
  }

  public prepareSignedExportAccessContracts(): Observable<string> {
    return this.accessContractApi.prepareSignedExportAccessContracts(this.headers);
  }
}
